import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import StarRating from "../components/StarRating";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PushPrompt from "../components/PushPrompt";
import SMSPreferencesCard from "../components/SMSPreferencesCard";
import { Lightning, MapPin, Clock, ChatCircle, CheckCircle, Wrench, CurrencyGbp, ShieldCheck } from "@phosphor-icons/react";

export default function TradesDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [feed, setFeed] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [range, setRange] = useState(user?.work_range_miles ?? 10);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/meta/trades").then(({ data }) => setTrades(data || []));
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        // Job feed: filter to trades user offers
        const items = await Promise.all(
          (user.trades || []).map((t) => api.get(`/jobs?trade=${t}&status=open`).then(r => r.data))
        );
        const merged = [].concat(...items);
        const dedup = Array.from(new Map(merged.map(j => [j.job_id, j])).values())
          .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
        setFeed(dedup);
        const q = await api.get("/quotes/mine").then(r => r.data);
        setMyQuotes(q);
      } finally { setLoading(false); }
    })();
  }, [user]);

  const saveRange = async () => {
    try {
      await api.patch("/me/profile", { work_range_miles: Number(range) });
      await refresh();
      toast.success("Work range updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const earnings = myQuotes.filter(q => q.status === "accepted").reduce((s, q) => s + q.amount, 0);

  const SubscriptionBanner = () => {
    if (user?.subscription_status === "active") return null;
    const expired = user?.subscription_status === "expired";
    return (
      <div className="brand-card p-5 noise-bg" style={{ background: expired ? "#7a1f1f" : "var(--brand-green)", color: "#fff" }} data-testid="subscription-banner">
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div>
            <p className="font-display font-bold text-lg">
              {expired ? "⚠️ Your membership has expired" : "Activate your £3.99/month membership"}
            </p>
            <p className="text-white/85 text-sm">
              {expired
                ? "You're no longer receiving job alerts and can't send quotes. Reactivate now to resume — it's still just £3.99/month."
                : "Get unlimited job alerts and start sending quotes."}
            </p>
          </div>
          <Link to="/subscribe" className="btn-accent" data-testid="banner-subscribe-btn">{expired ? "Reactivate" : "Activate now"}</Link>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 space-y-6" data-testid="trader-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>{user?.business_name || user?.name}</h1>
            <p className="text-[var(--text-muted)] mt-1">Your trade hub.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/profile" className="btn-accent" data-testid="trader-edit-profile">Edit profile</Link>
          </div>
        </div>

        <SubscriptionBanner />

        {user?.subscription_status === "active" && user?.stripe_customer_id && (
          <div className="brand-card p-4 flex items-center gap-3 flex-wrap" data-testid="manage-sub-card">
            <div className="flex-1">
              <p className="text-sm"><strong className="text-[var(--brand-green)]">Subscription active.</strong> Auto-renews monthly. Manage your card, view invoices or cancel anytime.</p>
            </div>
            <button onClick={async () => {
              try {
                const { data } = await api.post("/payments/portal", { return_url: window.location.href });
                window.location.href = data.url;
              } catch (e) { toast.error(formatApiError(e)); }
            }} className="btn-ghost text-sm" data-testid="manage-sub-btn">Manage subscription</button>
          </div>
        )}

        {/* Profile completion nudge */}
        {(!user?.bio || !user?.business_name || !user?.postcode || (user?.trades || []).length === 0 || (user?.portfolio || []).length === 0) && (
          <div className="brand-card p-4 flex items-center gap-3" data-testid="profile-incomplete-banner">
            <span className="text-2xl">👋</span>
            <div className="flex-1">
              <p className="font-display font-semibold text-[var(--brand-green)]">Complete your profile to win more jobs</p>
              <p className="text-sm text-[var(--text-muted)]">Add your business name, postcode, trades, work photos and a short bio — verified profiles win 3× more quotes.</p>
            </div>
            <Link to="/profile" className="btn-primary" data-testid="complete-profile-cta">Complete now</Link>
          </div>
        )}

        <PushPrompt />
        {user?.subscription_status === "active" && <SMSPreferencesCard user={user} />}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="brand-card p-4">
            <div className="text-xs text-[var(--text-muted)]">Matching jobs</div>
            <div className="font-display font-extrabold text-2xl text-[var(--brand-green)] mt-1">{feed.length}</div>
          </div>
          <div className="brand-card p-4">
            <div className="text-xs text-[var(--text-muted)]">Quotes sent</div>
            <div className="font-display font-extrabold text-2xl text-[var(--brand-green)] mt-1">{myQuotes.length}</div>
          </div>
          <div className="brand-card p-4">
            <div className="text-xs text-[var(--text-muted)]">Earnings (accepted)</div>
            <div className="font-display font-extrabold text-2xl text-[var(--brand-green)] mt-1">£{earnings.toFixed(2)}</div>
          </div>
          <div className="brand-card p-4">
            <div className="text-xs text-[var(--text-muted)]">Rating</div>
            <div className="mt-1"><StarRating value={user?.rating_avg || 0} count={user?.rating_count || 0} size={18} /></div>
          </div>
        </div>

        {/* Work range slider */}
        <div className="brand-card p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="font-display font-semibold text-[var(--brand-green)] flex items-center gap-2"><MapPin size={18} weight="duotone" /> Work range</h3>
              <p className="text-sm text-[var(--text-muted)]">Receive alerts up to <strong>{range} mile{range===1?"":"s"}</strong> from your postcode.</p>
            </div>
            <div className="flex-1 min-w-[250px] flex items-center gap-3">
              <input
                type="range" min={0} max={50} step={1} value={range}
                onChange={(e) => setRange(Number(e.target.value))}
                className="w-full accent-[var(--brand-orange)]"
                data-testid="range-slider"
              />
              <span className="font-display font-bold text-[var(--brand-green)] w-12 text-right">{range} mi</span>
              <button onClick={saveRange} className="btn-primary" data-testid="range-save-btn">Save</button>
            </div>
          </div>
        </div>

        {/* Job feed */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Job feed</h2>
            <Link to="/profile" className="text-sm text-[var(--brand-green)] underline">Edit my trades →</Link>
          </div>
          {loading ? <p className="text-[var(--text-muted)]">Loading…</p> :
            feed.length === 0 ? (
              <div className="brand-card p-8 text-center text-[var(--text-muted)]">No open jobs match your trades right now. Adjust your trades from your profile.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="job-feed">
                {feed.map((j) => (
                  <Link to={`/jobs/${j.job_id}`} key={j.job_id} className="brand-card p-5" data-testid={`feed-job-${j.job_id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <h3 className="font-display font-semibold text-lg text-[var(--brand-green)] line-clamp-1">{j.title}</h3>
                      {j.budget && <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-[rgba(226,109,41,0.12)] text-[var(--brand-orange)]">£{j.budget.toFixed(0)}</span>}
                    </div>
                    <p className="mt-1 text-sm text-[var(--text-muted)] line-clamp-2">{j.description}</p>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-[var(--text-muted)]">
                      <span className="inline-flex items-center gap-1"><MapPin size={12} /> {j.postcode}</span>
                      <span className="inline-flex items-center gap-1"><Wrench size={12} /> {j.trade}</span>
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> {new Date(j.created_at).toLocaleDateString()}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )
          }
        </section>

        {/* My quotes */}
        {myQuotes.length > 0 && (
          <section>
            <h2 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>My quotes</h2>
            <div className="mt-3 brand-card divide-y divide-[var(--border-stone)] overflow-hidden">
              {myQuotes.map((q) => (
                <Link to={`/jobs/${q.job_id}`} key={q.quote_id} className="block p-4 hover:bg-[#FAF8F2]" data-testid={`my-quote-${q.quote_id}`}>
                  <div className="flex justify-between items-center gap-3">
                    <div>
                      <div className="font-display font-semibold text-[var(--brand-green)]">{q.job_title || "Job"}</div>
                      <div className="text-xs text-[var(--text-muted)]">{q.job_postcode} · {new Date(q.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-[var(--brand-green)]">£{q.amount.toFixed(2)}</div>
                      <div className="text-xs font-semibold uppercase tracking-wide" style={{
                        color: q.status === "accepted" ? "#0A7048" : q.status === "declined" ? "#8a3030" : "var(--brand-orange)"
                      }}>{q.status}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
